import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Target, ExternalLink, Star, TrendingUp } from "lucide-react";
import { useGrowthOnePagerEditor } from "@/contexts/GrowthOnePagerEditorContext";
import { Link } from "react-router-dom";

interface GrowthPillarCardProps {
  pillar: {
    id: string;
    name: string;
    description: string | null;
    created_at: string;
  };
  salesChannelId: string;
  order: number;
}

const GrowthPillarCard = ({ pillar, salesChannelId, order }: GrowthPillarCardProps) => {
  const { getMetricsForPillar } = useGrowthOnePagerEditor();
  const growthMetrics = getMetricsForPillar(pillar.id);
  
  const northStarMetrics = growthMetrics.filter(metric => metric.category === 'North Star');
  const supportiveMetrics = growthMetrics.filter(metric => metric.category === 'Supportive Metric');

  return (
    <Card className="relative overflow-hidden border-l-4 border-l-blue-500 bg-white shadow-sm hover:shadow-md transition-shadow">
      {/* Corner Triangle with Order Number */}
      <div className="absolute top-0 left-0 w-10 h-10 overflow-hidden">
        <div className="absolute top-0 left-0 w-0 h-0 border-l-[40px] border-l-blue-500 border-b-[40px] border-b-transparent" />
        <span className="absolute top-1 left-1 text-[10px] font-bold text-white">
          {order.toString().padStart(2, '0')}
        </span>
      </div>
      <CardHeader className="pb-2 pt-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <Target className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <CardTitle className="text-sm font-medium">{pillar.name}</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="h-6 w-6 p-0"
          >
            <Link to={`/growth-pillars/${salesChannelId}/metrics/${pillar.id}`}>
              <ExternalLink className="h-3 w-3" />
            </Link>
          </Button>
        </div>
        {pillar.description && (
          <p className="text-xs text-muted-foreground mt-1">{pillar.description}</p>
        )}
      </CardHeader>
      
      <CardContent className="pt-0 pb-3">
        <div className="space-y-2">
          {/* North Star Metrics */}
          {northStarMetrics.length > 0 && (
            <div>
              <div className="flex items-center space-x-1 mb-1">
                <Star className="h-3 w-3 text-yellow-500" />
                <span className="text-xs font-medium">North Star</span>
              </div>
              <div className="space-y-1">
                {northStarMetrics.map((metric) => (
                  <div key={metric.id} className="text-xs text-slate-700 pl-4">
                    {metric.name}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Supportive Metrics */}
          {supportiveMetrics.length > 0 && (
            <div>
              <div className="flex items-center space-x-1 mb-1">
                <TrendingUp className="h-3 w-3 text-green-500" />
                <span className="text-xs font-medium">Supportive</span>
              </div>
              <div className="space-y-1">
                {supportiveMetrics.map((metric) => (
                  <div key={metric.id} className="text-xs text-slate-700 pl-4">
                    {metric.name}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* No Metrics State */}
          {growthMetrics.length === 0 && (
            <div className="text-center py-2">
              <p className="text-xs text-slate-500 mb-2">No metrics defined</p>
              <Button
                variant="outline"
                size="sm"
                asChild
                className="h-6 text-xs px-2"
              >
                <Link to={`/growth-pillars/${salesChannelId}/metrics/${pillar.id}`}>
                  Add Metrics
                </Link>
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default GrowthPillarCard;
